import React, { useMemo, useCallback } from "react";
import { Grid, Box, Card, CardMedia, Typography, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import imageNotFound from "../../../assets/card-image/product-not-found.jpg"
import ProductListSkeleton from "../../DASHBOARD/ProductListSkeleton";
import { capitalizeFirst, extractDiscount, toTitleCase } from "../../../hooks/utilsFn/utilsFn";
import NoProductFoundImage from '../../../assets/card-image/product-not-found.jpg'
const DesktopProductCardsList = ({ selectedCategory, products, loading }) => {
    const navigate = useNavigate();

    // Memoized filtered products
    const filteredProducts = useMemo(() => {
        if (!products?.length) return [];

        // If no category selected or selectedCategory is empty, show all products
        if (!selectedCategory || selectedCategory.length === 0) return products;

        // Filter products based on selected categories
        return products.filter(product =>
            selectedCategory.includes(product?.node?.category) ||
            selectedCategory.includes(product?.node?.categoryId)
        );
    }, [products, selectedCategory]);

    // Navigation handler
    const handleProductClick = useCallback((productId) => {
        navigate(`/card-details/${productId}`);
    }, [navigate]);

    // Extract product data helper
    const getProductData = useCallback((product) => ({
        id: product?.node?.id,
        slug: product?.node?.slug,
        name: product?.node?.name,
        logoUrl: product?.node?.productImages?.[0]?.url,
        discount: product?.node?.denominations?.[0]?.value,
        dicsountType: product?.node?.denominations?.[0]?.cashbackOrDiscount,
        category: product?.node?.category
    }), []);

    // No products found component
    const NoProductsFound = useMemo(() => (
        <Grid
            item
            xs={12}
            height="100vh"
            display="flex"
            justifyContent="center"
            alignItems="center"

        >
            <Box
                sx={{
                    textAlign: "center",
                    padding: "40px 32px",
                    width: "90%",
                    maxWidth: "400px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            >

                <Box
                    sx={{
                        width: "220px",
                        height: "220px",
                        marginBottom: "20px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        overflow: "hidden",
                        transition: "transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                        boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
                        "&:hover": {
                            transform: "scale(1.06)",
                        },
                    }}
                >
                    <Box
                        component="img"
                        src={NoProductFoundImage}
                        alt="No Products Found"
                        sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                        }}
                    />
                </Box>

                {/* Typography Section */}
                <Typography
                    sx={{
                        fontFamily: "Be Vietnam Pro",
                        fontSize: "22px",
                        fontWeight: 600,
                        color: "dark.main",
                        marginBottom: "8px",
                    }}
                >
                    No Products Found
                </Typography>

                <Typography
                    sx={{
                        fontFamily: "Be Vietnam Pro",
                        fontSize: "13.5px",
                        color: "#64748b",
                        lineHeight: 1.6,
                        marginBottom: "28px",
                        maxWidth: "280px",
                    }}
                >
                    We couldn't find what you're looking for. Try checking adjusting your filters.
                </Typography>

                {/* Premium Interactive CTA Button */}
                <Button
                    variant="contained"
                    onClick={() => navigate("/")}
                    sx={{
                        textTransform: "none",
                        width: '50%',
                        borderRadius: "12px",
                        background: "primary.main",
                        boxShadow: "0 4px 14px rgba(102, 126, 234, 0.4)",

                        fontWeight: 500,
                        fontFamily: "Be Vietnam Pro",
                        fontSize: "13px",
                        color: "#fff",

                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                            boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                            transform: "translateY(-2px)",
                        },
                        "&:active": {
                            transform: "translateY(0px)",
                        }
                    }}
                >
                    Go to Home
                </Button>
            </Box>
        </Grid>
    ), [navigate]);


    const ProductCard = useMemo(() => {
        return ({ product }) => {
            const { id, slug, name, logoUrl, dicsountType } = getProductData(product);
            const discount = extractDiscount(product?.node?.denominations?.[0]);
            const symbol = dicsountType === "CASHBACK" ? "AED" : "%"

            return (
                <Box
                    key={id}
                    onClick={() => handleProductClick(slug)}
                    sx={{
                        width: "100%",
                        cursor: "pointer",

                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            height: 250,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            borderRadius: "10px",
                            overflow: "hidden",
                            backgroundColor: "#fff",
                            boxShadow: '0 2px 6px rgba(77, 18, 18, 0.08)',
                        }}
                    >
                        <Card
                            sx={{
                                width: "100%",
                                height: 150,
                                borderRadius: 0,
                                boxShadow: "none",
                                overflow: "hidden",
                                flexShrink: 0,
                            }}
                        >
                            <CardMedia
                                component="img"
                                image={logoUrl || imageNotFound}
                                alt={name}
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    display: "block",
                                }}
                            />
                        </Card>

                        <Typography
                            sx={{

                                px: 1,
                                fontWeight: 500,
                                fontSize: "12px",
                                color: "#2B3445",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                fontFamily: "Be Vietnam Pro",
                            }}
                        >
                            {toTitleCase(name)}

                        </Typography>

                        <Typography
                            sx={{
                                px: 1,
                                color: "#2E7D32",
                                fontWeight: 600,
                                fontSize: "12px",
                                fontFamily: "Be Vietnam Pro",
                            }}
                        >
                            {discount} {symbol}{" "}
                            {capitalizeFirst(dicsountType)}
                        </Typography>

                        <Box sx={{ px: 1.5, mb: 1 }}>
                            <Button
                                fullWidth
                                sx={{
                                    height: 30,
                                    borderRadius: "5px",
                                    borderColor: "#C5CAD3",
                                    color: "#2B3445",
                                    border: "1px solid #002865",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    fontSize: "12px",
                                    fontFamily: "Be Vietnam Pro",
                                }}
                            >
                                View Card
                            </Button>
                        </Box>
                    </Box>
                </Box>

            );
        };
    }, [getProductData, handleProductClick]);

    if (loading) {
        return <ProductListSkeleton />;
    }

    // Early return if no products
    if (!filteredProducts?.length) {
        return (
            <Grid container spacing={2} mb={4}>
                {NoProductsFound}
            </Grid>
        );
    }

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                gap: 1,
                mb: 4,
            }}
        >
            {
                filteredProducts.map((product) => (
                    <ProductCard key={product?.node?.id} product={product} />
                ))
            }
        </Box>
    );
};


export default React.memo(DesktopProductCardsList);
